/**
 * Registro del ESQUEMA ACTIVO (composition root liviano).
 *
 * El host inyecta su `VariableSchema` al arranque con `setActiveSchema(...)` (típicamente
 * desde `VariableSchemaProvider.getSchema()`). El engine y su UI leen de acá los artefactos
 * derivados (símbolos, oráculo de validez, preview), memoizados por identidad de esquema.
 *
 * Es la costura de inyección de dependencias: la UI del editor no importa el dominio,
 * pregunta acá. Si nadie setea esquema, devuelve uno vacío (no rompe).
 */

import type { VariableSchema, VariableType } from '@/shared/variables/variable-schema'
import {
  buildInsertableSymbols,
  buildKnownTokens,
  buildLabelByToken,
  buildPreviewValues,
  buildTypeByToken,
  type InsertableSymbol
} from '@/shared/variables/variable-symbols'

const EMPTY_SCHEMA: VariableSchema = {
  scalars: [],
  collections: [],
  legacyTokens: [],
  previewValues: {}
}

let activeSchema: VariableSchema = EMPTY_SCHEMA

interface DerivedArtifacts {
  schema: VariableSchema
  symbols: InsertableSymbol[]
  labelByToken: Map<string, string>
  knownTokens: Set<string>
  previewValues: Record<string, string>
  typeByToken: Map<string, VariableType>
  collectionAliases: Set<string>
}
let memo: DerivedArtifacts | null = null

// Alias de las colecciones iterables (incluye las anidadas de 1 nivel). Sirve para
// distinguir un campo de LOOP real (cliente, cuota, vehiculo…) de un escalar cuyo
// token igual tiene punto (ej. `representante.nombre`): solo los primeros llevan
// color de scope. Pura derivación del esquema.
const buildCollectionAliases = (schema: VariableSchema): Set<string> => {
  const out = new Set<string>()
  for (const c of schema.collections) {
    out.add(c.alias)
    for (const child of c.children ?? []) out.add(child.alias)
  }
  return out
}

export const setActiveSchema = (schema: VariableSchema): void => {
  activeSchema = schema
  memo = null // invalida el memo derivado
}

export const getActiveSchema = (): VariableSchema => activeSchema

const derived = (): DerivedArtifacts => {
  if (memo && memo.schema === activeSchema) return memo
  const symbols = buildInsertableSymbols(activeSchema)
  memo = {
    schema: activeSchema,
    symbols,
    labelByToken: buildLabelByToken(symbols),
    knownTokens: buildKnownTokens(activeSchema, symbols),
    previewValues: buildPreviewValues(activeSchema),
    typeByToken: buildTypeByToken(symbols),
    collectionAliases: buildCollectionAliases(activeSchema)
  }
  return memo
}

export const getInsertableSymbols = (): InsertableSymbol[] => derived().symbols
export const getLabelByToken = (): Map<string, string> => derived().labelByToken
export const getKnownTokens = (): Set<string> => derived().knownTokens
export const getPreviewValues = (): Record<string, string> => derived().previewValues
export const getLegacyTokens = (): string[] => activeSchema.legacyTokens ?? []
export const getCollectionAliases = (): Set<string> => derived().collectionAliases

// Tipo declarado de un token (escalar o `alias.campo`), del esquema activo. Lo usa el
// evaluador de fórmulas para parsear fechas/números. undefined si no está tipado.
export const getVariableType = (token: string): VariableType | undefined =>
  derived().typeByToken.get(token)
